/**
 * Capacitor Platform Bridge Utility
 * 
 * Platform algılama, status bar kontrolü, haptic feedback gibi
 * native özelliklere erişim sağlayan yardımcı fonksiyonlar.
 * 
 * Web platformunda güvenle çağrılabilir — native olmayan ortamlarda
 * sessizce atlayarak hata vermez.
 */
import { Capacitor } from '@capacitor/core';

/** Cihaz native platform mı (iOS/Android)? */
export const isNativePlatform = () => Capacitor.isNativePlatform();

/** iOS native mi? */
export const isIOS = () => Capacitor.getPlatform() === 'ios';

/** Gerçek iOS native uygulaması içinde mi çalışıyor (Capacitor WKWebView)? */
export const isNativeIOS = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

/** Android native mi? */
export const isAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Google Play'den yüklenen Android TWA uygulaması içinde mi?
 * İlk açılışta referrer "android-app://com.kapihoca.kpss" olur (LauncherActivity ayrıca native_fcm_token ekler).
 * Google giriş yönlendirmesinden dönünce referrer değişir; bu yüzden sekme ömrü boyunca sessionStorage'da tutulur.
 * localStorage KULLANMA: TWA, Chrome ile aynı depolamayı paylaşır, normal Chrome ziyaretçisi de "uygulama" sanılır.
 */
export const isAndroidTwa = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        if (sessionStorage.getItem('kpss_twa') === '1') return true;
        const fromTwa = document.referrer.startsWith('android-app://com.kapihoca.kpss')
            || new URLSearchParams(window.location.search).has('native_fcm_token');
        if (fromTwa) sessionStorage.setItem('kpss_twa', '1');
        return fromTwa;
    } catch {
        return false;
    }
};

/** Mağazadan yüklenen uygulama mı (iOS Capacitor veya Android TWA)? Tanıtım sayfası yerine ders seçim ekranı açılır. */
export const isAppShell = () => isNativePlatform() || isAndroidTwa() || getScreenshotPlatform() !== null;

/**
 * Mağaza ekran görüntüsü modu: ?shot=ios ile açılan web sayfası iPhone uygulaması gibi görünür
 * (tanıtım sayfası yerine ders seçim ekranı, abonelik butonu "App Store ile Abone Ol").
 * Yalnızca görünümü değiştirir; satın alma yapılamaz (storeBilling.ts). Sekme ömrü boyunca hatırlanır.
 */
export const getScreenshotPlatform = (): 'ios' | null => {
    if (typeof window === 'undefined') return null;
    try {
        if (new URLSearchParams(window.location.search).get('shot') === 'ios') sessionStorage.setItem('kpss_shot', 'ios');
        return sessionStorage.getItem('kpss_shot') === 'ios' ? 'ios' : null;
    } catch {
        return null;
    }
};

/** Web (tarayıcı) mı? */
export const isWeb = () => Capacitor.getPlatform() === 'web';

/**
 * Hafif dokunsal geri bildirim tetikler (iOS/Android).
 * Web'de sessizce atlanır.
 */
export const triggerHaptic = async () => {
    if (isNativePlatform()) {
        try {
            const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
            await Haptics.impact({ style: ImpactStyle.Light });
        } catch (e) {
            console.warn('[CapacitorBridge] Haptic feedback error:', e);
        }
    }
};

/**
 * iOS status bar'ı koyu tema olarak yapılandırır.
 * Android'de TWA zaten status bar'ı yönetir.
 */
export const configureStatusBar = async () => {
    if (isIOS()) {
        try {
            const { StatusBar, Style } = await import('@capacitor/status-bar');
            await StatusBar.setStyle({ style: Style.Dark });
            await StatusBar.setBackgroundColor({ color: '#0F172A' });
        } catch (e) {
            console.warn('[CapacitorBridge] StatusBar config error:', e);
        }
    }
};
